import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Typography,
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
    Paper,
    Divider,
    CircularProgress,
    Alert,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
    FormLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import { loadStripe } from '@stripe/stripe-js';
import { subscriptionAPI, couponAPI } from '../services/api';
import api from '../services/api';
import { trackServerEvent } from '../utils/analytics';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null;

const PricingTier = ({ 
    title, 
    monthlyPrice, 
    annualPrice, 
    features, 
    recommended, 
    icon: IconComponent,
    planType 
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [teamSize, setTeamSize] = useState(5);
    const [attemptedSubscription, setAttemptedSubscription] = useState(false);

    const handleSubscribe = async (startTrial = false) => {
        if (!isAuthenticated) {
            navigate(`/register?planType=${planType}&billingCycle=${startTrial ? 'trial' : billingCycle}${planType === 'team' ? `&teamSize=${teamSize}` : ''}`);
            return;
        }

        if (isAuthenticated) trackServerEvent(startTrial ? 'trial_started' : 'subscription_started', { planType, billingCycle });

        try {
            setLoading(true);
            setError(null);
            
            console.log('Subscription attempt:', { planType, billingCycle: startTrial ? 'trial' : billingCycle });

            const { sessionId } = await subscriptionAPI.createCheckoutSession(
                planType,
                startTrial ? 'trial' : billingCycle,
                startTrial ? planType : null,
                planType === 'team' ? teamSize : null
            );
            
            console.log('Received sessionId:', sessionId);
            
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId });
            
            if (error) {
                console.error('Stripe redirectToCheckout error:', error);
                throw error;
            }
        } catch (err) {
            console.error('Subscription error details:', err.response?.data || err.message || err);
            setError('Failed to process subscription. Please try again.');
            console.error('Subscription error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (location?.search) {
            const params = new URLSearchParams(location.search);
            const urlPlanType = params.get('planType');
            const urlBillingCycle = params.get('billingCycle');
            const urlTeamSize = params.get('teamSize');
            const justRegistered = params.get('registered');
            
            if (urlPlanType === planType && urlBillingCycle && justRegistered === 'true' && isAuthenticated && !attemptedSubscription) {
                if (urlBillingCycle && (urlBillingCycle === 'monthly' || urlBillingCycle === 'annual')) {
                    setBillingCycle(urlBillingCycle);
                }
                
                if (urlTeamSize && planType === 'team') {
                    setTeamSize(parseInt(urlTeamSize));
                }
                
                // Set the flag to prevent multiple attempts
                setAttemptedSubscription(true);
                
                // Remove the 'registered' parameter from the URL to prevent infinite loop
                const newParams = new URLSearchParams(params);
                newParams.delete('registered');
                const newSearch = newParams.toString();
                const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
                navigate(newPath, { replace: true });
                
                setTimeout(() => {
                    handleSubscribe(urlBillingCycle === 'trial');
                }, 500);
            }
        }
    }, [location, isAuthenticated, planType, handleSubscribe, attemptedSubscription, navigate]);

    const handleBillingCycleChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const handleTeamSizeChange = (event) => {
        const size = Math.max(5, parseInt(event.target.value) || 5);
        setTeamSize(size);
    };

    const currentPrice = billingCycle === 'monthly' ? monthlyPrice : annualPrice;

    const displayPrice = () => {
        if (planType === 'team') {
            return `$${(currentPrice * teamSize).toFixed(2)}/month`;
        }
        return billingCycle === 'monthly' 
            ? `$${currentPrice}/month`
            : `$${currentPrice}/year`;
    };

    const getButtonText = (isTrialButton = false) => {
        if (!isAuthenticated) {
            return isTrialButton ? "Register to Start Trial" : "Register to Subscribe";
        }
        return isTrialButton ? "Start 3-Day Trial" : `Get ${title}`;
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transform: recommended ? 'scale(1.05)' : 'none',
                border: recommended ? `2px solid ${theme.palette.primary.main}` : 'none',
                '&:hover': {
                    transform: recommended ? 'scale(1.07)' : 'scale(1.02)',
                    transition: 'transform 0.3s ease-in-out'
                }
            }}
        >
            {recommended && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                    }}
                >
                    Most Popular
                </Box>
            )}
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconComponent color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h2">
                        {title}
                    </Typography>
                </Box>

                {planType !== 'team' && (
                    <Box sx={{ mb: 2 }}>
                        <ToggleButtonGroup
                            value={billingCycle}
                            exclusive
                            onChange={handleBillingCycleChange}
                            size="small"
                            fullWidth
                        >
                            <ToggleButton value="monthly">Monthly</ToggleButton>
                            <ToggleButton value="annual">
                                Annual
                                <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                    Save 20%
                                </Typography>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {planType === 'team' && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Number of Users (min. 5)
                        </Typography>
                        <TextField
                            type="number"
                            value={teamSize}
                            onChange={handleTeamSizeChange}
                            inputProps={{ min: 5 }}
                            size="small"
                            fullWidth
                        />
                    </Box>
                )}

                <Typography variant="h4" component="p" sx={{ mb: 2 }}>
                    {displayPrice()}
                </Typography>

                <List>
                    {features.map((feature, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                        </ListItem>
                    ))}
                </List>
            </CardContent>

            <CardActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleSubscribe(false)}
                    disabled={loading}
                >
                    {loading ? "Processing..." : getButtonText()}
                </Button>
                
                {(planType === 'basic' || planType === 'pro') && (
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleSubscribe(true)}
                        disabled={loading}
                        className="trial-button"
                        sx={{
                            '@keyframes pulse': {
                                '0%': {
                                    boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)'
                                },
                                '70%': {
                                    boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)'
                                },
                                '100%': {
                                    boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)'
                                }
                            }
                        }}
                    >
                        {loading ? "Processing..." : `Try 3 Days for $${planType === 'basic' ? '1' : '2'}`}
                    </Button>
                )}
                
                {error && (
                    <Typography color="error" variant="caption">
                        {error}
                    </Typography>
                )}
            </CardActions>
        </Card>
    );
};

const Subscription = () => {
    const theme = useTheme();
    const isMobile = theme.breakpoints.down('sm');
    const { user, forceRefreshUserData, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    
    const [startTrial, setStartTrial] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [planType, setPlanType] = useState('basic');
    const [teamSize, setTeamSize] = useState(5);
    const [error, setError] = useState(null);
    const [loadingSubscription, setLoadingSubscription] = useState(true);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [subscriptionCheckError, setSubscriptionCheckError] = useState(null);
    
    const location = useLocation();
    const showUpgradeBanner = new URLSearchParams(location.search).get('upgrade') === 'true';
    const showExpiredBanner = new URLSearchParams(location.search).get('expired') === 'true';
    const showFeedbackForm = new URLSearchParams(location.search).get('feedback') === '1';

    const [feedbackReason, setFeedbackReason] = useState('');
    const [feedbackDetail, setFeedbackDetail] = useState('');
    const [feedbackWouldReturn, setFeedbackWouldReturn] = useState(null);
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [feedbackError, setFeedbackError] = useState(null);

    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState(null);
    const [couponSuccess, setCouponSuccess] = useState(null);
    const [showCouponInput, setShowCouponInput] = useState(false);

    const loadCurrentSubscription = useCallback(async () => {
        if (!isAuthenticated) {
            setCurrentSubscription(null);
            setSubscriptionCheckError(null);
            setLoadingSubscription(false);
            return { subscription: null, notFound: true };
        }

        setLoadingSubscription(true);
        setSubscriptionCheckError(null);
        try {
            const subscription = await subscriptionAPI.getCurrentSubscription();
            setCurrentSubscription(subscription);
            return { subscription, notFound: false };
        } catch (err) {
            setCurrentSubscription(null);
            if (err.response?.status === 404) {
                return { subscription: null, notFound: true };
            }

            setSubscriptionCheckError('We could not verify your membership. Please retry before starting another subscription.');
            return { subscription: null, notFound: false };
        } finally {
            setLoadingSubscription(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!authLoading) loadCurrentSubscription();
    }, [authLoading, loadCurrentSubscription]);

    // Track pricing page view
    useEffect(() => {
        if (isAuthenticated) trackServerEvent('pricing_page_viewed', { source: showUpgradeBanner ? 'upgrade_banner' : 'direct' });
    }, [isAuthenticated, showUpgradeBanner]);

    // Auto-fill coupon from URL param (e.g., win-back email links)
    useEffect(() => {
        const urlCoupon = new URLSearchParams(location.search).get('coupon');
        if (urlCoupon) {
            setCouponCode(urlCoupon);
            setShowCouponInput(true);
        }
    }, [location]);

    const handleRedeemCoupon = async () => {
        const code = couponCode.trim();
        if (!code) return;
        setCouponLoading(true);
        setCouponError(null);
        setCouponSuccess(null);
        try {
            const result = await couponAPI.redeem(code);
            setCouponSuccess(result?.message || 'Code redeemed! Redirecting...');
            setCouponCode('');
            await forceRefreshUserData();
            setTimeout(() => navigate('/dashboard'), 1500);
        } catch (err) {
            setCouponError(err.response?.data?.message || 'Invalid or expired code.');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleFeedbackSubmit = async () => {
        if (!feedbackReason) {
            setFeedbackError('Please select a reason.');
            return;
        }
        setFeedbackSubmitting(true);
        setFeedbackError(null);
        try {
            await api.post('/subscription/cancellation-feedback', {
                reason: feedbackReason,
                detail: feedbackDetail,
                wouldReturn: feedbackWouldReturn === 'yes',
            });
            setFeedbackSubmitted(true);
        } catch (err) {
            setFeedbackError(err.response?.data?.error || 'Failed to submit feedback.');
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    const handleForceRefresh = async () => {
        setIsLoading(true);
        try {
            const refreshedUser = await forceRefreshUserData();
            console.log('User data refreshed from server:', refreshedUser);

            const { subscription, notFound } = await loadCurrentSubscription();
            if (subscription) {
                alert('Current subscription found! Redirecting to dashboard...');
                setTimeout(() => navigate('/dashboard'), 1500);
            } else if (notFound && ['active', 'trialing'].includes(refreshedUser?.subscription?.status)) {
                setSubscriptionCheckError('Your account shows active access, but no current billing subscription was found. Please contact support before purchasing again.');
            }
        } catch (err) {
            console.error('Error refreshing user data:', err);
            setError('Failed to refresh user data');
        } finally {
            setIsLoading(false);
        }
    };

    const profileShowsActiveAccess = ['active', 'trialing'].includes(user?.subscription?.status);
    const hasUnverifiedActiveAccess = isAuthenticated
        && !authLoading
        && !loadingSubscription
        && !currentSubscription
        && !subscriptionCheckError
        && profileShowsActiveAccess;
    const shouldShowPricing = !authLoading && (
        !isAuthenticated
        || (!loadingSubscription && !currentSubscription && !subscriptionCheckError && !profileShowsActiveAccess)
    );
    const currentPeriodEnd = currentSubscription?.endDate ? new Date(currentSubscription.endDate) : null;
    const currentPeriodEndLabel = currentPeriodEnd && !Number.isNaN(currentPeriodEnd.getTime())
        ? currentPeriodEnd.toLocaleDateString()
        : null;
    const autoRenewEnabled = currentSubscription?.autoRenew !== false;
    
    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            {showExpiredBanner && !currentSubscription && (
                <Alert severity="warning" sx={{ mb: 3, fontSize: '1rem' }}>
                    Your free access period has expired. Enter a new code or subscribe below to keep reading.
                </Alert>
            )}
            {showUpgradeBanner && !currentSubscription && (
                <Alert severity="info" sx={{ mb: 3, fontSize: '1rem' }}>
                    A subscription is required to use that feature. Start a 3-day trial for just $1 or subscribe below.
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            {isAuthenticated && !authLoading && loadingSubscription && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Checking your current membership before showing checkout options...
                </Alert>
            )}
            {subscriptionCheckError && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                    action={<Button color="inherit" size="small" onClick={loadCurrentSubscription}>Retry</Button>}
                >
                    {subscriptionCheckError}
                </Alert>
            )}
            {hasUnverifiedActiveAccess && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                    action={<Button color="inherit" size="small" onClick={loadCurrentSubscription}>Recheck</Button>}
                >
                    Your account is marked active, but no current billing subscription was found. Checkout is hidden to avoid a possible duplicate charge.
                </Alert>
            )}

            {isAuthenticated && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography
                        component="span"
                        onClick={() => setShowCouponInput(!showCouponInput)}
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationColor: theme.palette.divider,
                            '&:hover': { color: 'text.primary' },
                        }}
                    >
                        Have a code?
                    </Typography>
                    {showCouponInput && (
                        <Paper elevation={0} sx={{ p: 2.5, mt: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, maxWidth: 400, mx: 'auto' }}>
                            {couponSuccess && <Alert severity="success" sx={{ mb: 2 }}>{couponSuccess}</Alert>}
                            {couponError && <Alert severity="error" sx={{ mb: 2 }}>{couponError}</Alert>}
                            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Coupon Code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleRedeemCoupon(); }}
                                    disabled={couponLoading}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleRedeemCoupon}
                                    disabled={couponLoading || !couponCode.trim()}
                                    sx={{ minWidth: 100, whiteSpace: 'nowrap' }}
                                >
                                    {couponLoading ? <CircularProgress size={22} /> : 'Redeem'}
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>
            )}
            
            {/* Subscription refresh debug button - only for admins */}
            {user && user.username === 'jay' && (
                <Box sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px dashed #999' }}>
                        <Typography variant="subtitle2" gutterBottom>Admin Debugging Tools</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2">
                                Current user: {user.username} | 
                                Subscription: {user.subscription ? user.subscription.status : 'none'} |
                                Local storage data may be out of sync with server
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                size="small"
                                disabled={isLoading}
                                onClick={handleForceRefresh}
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Force Refresh User Data'}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}
            
            {currentSubscription && (
                <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, mb: 5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="overline" color="primary">Your membership</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {currentSubscription.status === 'trial' ? 'Your trial is active' : 'Your membership is active'}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {currentSubscription.planType === 'team' ? 'ReaderFLEX Team' : 'ReaderFLEX Membership'}
                        {currentSubscription.billingCycle ? ` · ${currentSubscription.billingCycle}` : ''}
                    </Typography>
                    {currentPeriodEndLabel && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {currentSubscription.status === 'trial' ? 'Trial ends' : 'Current billing period ends'} {currentPeriodEndLabel}
                        </Typography>
                    )}
                    <Alert severity={autoRenewEnabled ? 'success' : 'info'} sx={{ mb: 2 }}>
                        {autoRenewEnabled
                            ? currentSubscription.status === 'trial'
                                ? 'Your trial is set to convert to a paid membership automatically.'
                                : 'Your membership is set to renew automatically.'
                            : 'Automatic renewal is turned off.'}
                    </Alert>
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                        Go to dashboard
                    </Button>
                </Paper>
            )}

            {shouldShowPricing && (
                <>
                    <Box textAlign="center" mb={8}>
                        <Typography variant="h2" component="h1" gutterBottom>
                            ReaderFLEX Membership
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Start your journey to faster reading and better comprehension with ReaderFLEX
                        </Typography>
                        <Button
                            variant="text"
                            color="primary"
                            sx={{ mt: 2 }}
                            onClick={() => {
                                const membershipPlanElement = document.getElementById('membership-plan');
                                if (membershipPlanElement) {
                                    membershipPlanElement.scrollIntoView({ behavior: 'smooth' });
                                    const trialButton = membershipPlanElement.querySelector('.trial-button');
                                    if (trialButton) {
                                        trialButton.style.animation = 'pulse 1s';
                                    }
                                }
                            }}
                        >
                            TRY READERFLEX FOR JUST $1 FOR 3 DAYS! →
                        </Button>
                        {!isAuthenticated && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                New to ReaderFLEX? You'll need to create an account to subscribe.
                            </Typography>
                        )}
                    </Box>

                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={6}>
                            <Box id="membership-plan">
                                <PricingTier
                                    title="ReaderFLEX Membership"
                                    monthlyPrice={9.99}
                                    annualPrice={79.99}
                                    features={[
                                        'Unlimited AI-generated articles',
                                        'Speed reading training in 16 languages',
                                        'Comprehension quizzes with every article',
                                        'Vocabulary bank with spaced-repetition flashcards',
                                        'Full progress dashboard with weekly trends',
                                        '94 badges across 17 categories',
                                        'Leaderboard access',
                                        'Affiliate program (earn $3.00/month per referral)'
                                    ]}
                                    recommended={true}
                                    icon={StarIcon}
                                    planType="basic"
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box textAlign="center" mt={6}>
                        <Typography variant="body2" color="text.secondary">
                            The $1 trial gives you full access to every feature for 3 days. After your trial, your membership automatically continues at $9.99/month. Cancel anytime.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Cancel your subscription at any time. You keep access until the end of your billing period.
                        </Typography>
                    </Box>
                </>
            )}

            {/* Cancellation Feedback Survey Dialog */}
            <Dialog
                open={showFeedbackForm && !feedbackSubmitted}
                onClose={() => navigate('/subscription')}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>We'd love your feedback</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Sorry to see you go. Mind telling us why you canceled? This helps us improve.
                    </Typography>
                    {feedbackError && <Alert severity="error" sx={{ mb: 2 }}>{feedbackError}</Alert>}
                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <FormLabel component="legend" sx={{ mb: 1 }}>Why did you cancel?</FormLabel>
                        <RadioGroup value={feedbackReason} onChange={(e) => setFeedbackReason(e.target.value)}>
                            <FormControlLabel value="too_expensive" control={<Radio />} label="Too expensive" />
                            <FormControlLabel value="not_using_enough" control={<Radio />} label="Not using it enough" />
                            <FormControlLabel value="missing_features" control={<Radio />} label="Missing features I needed" />
                            <FormControlLabel value="found_alternative" control={<Radio />} label="Found an alternative" />
                            <FormControlLabel value="didnt_meet_expectations" control={<Radio />} label="Didn't meet my expectations" />
                            <FormControlLabel value="just_trying_it_out" control={<Radio />} label="Was just trying it out" />
                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Anything else you'd like to share? (optional)"
                        value={feedbackDetail}
                        onChange={(e) => setFeedbackDetail(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ mb: 1 }}>Would you consider coming back?</FormLabel>
                        <RadioGroup value={feedbackWouldReturn} onChange={(e) => setFeedbackWouldReturn(e.target.value)}>
                            <FormControlLabel value="yes" control={<Radio />} label="Yes, maybe" />
                            <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => navigate('/subscription')}>Skip</Button>
                    <Button
                        variant="contained"
                        onClick={handleFeedbackSubmit}
                        disabled={feedbackSubmitting}
                    >
                        {feedbackSubmitting ? <CircularProgress size={20} /> : 'Submit Feedback'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Thank you dialog after submission */}
            <Dialog
                open={feedbackSubmitted}
                onClose={() => navigate('/dashboard')}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Thank you</DialogTitle>
                <DialogContent>
                    <Alert severity="success">
                        Your feedback has been recorded. We appreciate you taking the time to share it.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => navigate('/dashboard')} variant="contained">Back to Dashboard</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Subscription;
